import axios from 'axios';

// Get shop domain from URL or use default
const getShopDomain = () => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('shopDomain') || 'universal.zonstaff.com';
};

// Create an axios instance with default config
const baseURL = import.meta.env.DEV ? '/api' : (import.meta.env.VITE_API_BASE_URL || 'https://api.zonstaff.com/api');

const api = axios.create({
  baseURL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
});

// Add a request interceptor
api.interceptors.request.use(
  config => {
    // Add auth token if available
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add universal app identifier
    config.headers['X-App-Type'] = 'universal';
    config.headers['X-App-Domain'] = getShopDomain();

    // Same reason as in components/axiosClient.jsx: a bodyless request that
    // still announces "Content-Type: application/json" makes Laravel read
    // $request->input() out of the empty body rather than the query string, so
    // every query parameter disappears server side. Axios sets the header
    // itself when there really is a JSON body.
    if (config.data === undefined || config.data === null) {
      if (typeof config.headers?.delete === 'function') {
        config.headers.delete('Content-Type');
      } else {
        delete config.headers['Content-Type'];
      }
    }

    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Add a response interceptor
api.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    // Handle common errors here
    if (error.response) {
      // Handle authentication errors
      if (error.response.status === 401) {
        localStorage.removeItem('authToken');
        sessionStorage.removeItem('authToken');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// API service functions
const apiService = {
  // Authentication functions
  login: (credentials) => {
    return api.post('/auth/login', {
      ...credentials,
      shopDomain: getShopDomain()
    });
  },

  signup: (signupData) => {
    return api.post('/auth/signup', {
      ...signupData,
      shopDomain: getShopDomain()
    });
  },

  // Sign in / sign up with Google. `companyName` is only needed the second time,
  // when the backend asks for it for a brand-new account.
  googleAuth: (credential, companyName = '') => {
    return api.post('/auth/google', { credential, companyName });
  },

  logout: () => {
    return api.post('/auth/logout', {
      shopDomain: getShopDomain()
    });
  },

  // Staff management functions
  getStaff: () => {
    return api.get('/staff', {
      params: { shopDomain: getShopDomain() }
    });
  },

  createStaff: (staffData) => {
    return api.post('/staff', {
      ...staffData,
      shopDomain: getShopDomain()
    });
  },

  updateStaff: (staffId, staffData) => {
    return api.put(`/staff/${staffId}`, {
      ...staffData,
      shopDomain: getShopDomain()
    });
  },

  deleteStaff: (staffId) => {
    return api.delete(`/staff/${staffId}`, {
      data: { shopDomain: getShopDomain() }
    });
  },



  // Payroll functions
  getPayrolls: () => {
    return api.get('/payrolls', {
      params: { shopDomain: getShopDomain() }
    });
  },

  createPayroll: (payrollData) => {
    return api.post('/payrolls', {
      ...payrollData,
      shopDomain: getShopDomain()
    });
  },

  // Settings functions
  getSettings: () => {
    return api.get('/settings', {
      params: { shopDomain: getShopDomain() }
    });
  },

  updateSettings: (settingsData) => {
    return api.put('/settings', {
      ...settingsData,
      shopDomain: getShopDomain()
    });
  },

  // Test authentication endpoint
  testAuth: () => {
    return api.get('/auth/test');
  },

  // Generic function for any action
  performAction: (action, data = {}) => {
    return api.post(`/${action}`, {
      ...data,
      shopDomain: getShopDomain(),
      timestamp: new Date().toISOString()
    });
  }
};

export default apiService;